/**
 * The engine oil dipstick height (MB's R150, decision 920; docs/specs/project-reporting/day-entry-card.md §3.2): the
 * oil level read on the dipstick before oil is added, optional, one per engine on a twin, beside the oil boxes of
 * Teslyn Field's Daily entry and Teslyn Mobile's New flight. Its unit is set per aircraft in the aircraft register
 * (`oil_dipstick_unit`, for example quarts), because dipsticks differ by engine. Shown on the entry and in Fuel and
 * maintenance ("Engine oil", `engineOilRows`); never on the client daily report or in the TJR export.
 */
import { entryShape } from './entry-card.js';
import type { Aircraft, DailyEntry, DipstickUnit } from './model.js';

/** The units the register offers, in its order. */
export const DIPSTICK_UNITS: readonly DipstickUnit[] = ['qt', 'L', 'cm', 'mm', 'in'];

/** The register's words for each unit. */
export const DIPSTICK_UNIT_LABEL: Readonly<Record<DipstickUnit, string>> = {
  qt: 'US quarts (qt)',
  L: 'Litres (L)',
  cm: 'Centimetres (cm)',
  mm: 'Millimetres (mm)',
  in: 'Inches (in)',
};

/** The highest reading each unit takes: well above any survey aircraft's sump (a PT6A holds 14 US quarts). */
export const DIPSTICK_MAX: Readonly<Record<DipstickUnit, number>> = {
  qt: 20,
  L: 20,
  cm: 50,
  mm: 500,
  in: 20,
};

/** The highest reading while the aircraft has no unit set. */
export const DIPSTICK_MAX_NO_UNIT = 500;

export function isDipstickUnit(v: unknown): v is DipstickUnit {
  return typeof v === 'string' && (DIPSTICK_UNITS as readonly string[]).includes(v);
}

/** The aircraft's dipstick unit, or null when the register has none. */
export function dipstickUnit(a: Aircraft | null | undefined): DipstickUnit | null {
  const u = a?.oil_dipstick_unit;
  return isDipstickUnit(u) ? u : null;
}

/** Which box: the single's, or the left or right engine's on a twin. */
export type DipstickEngine = 'single' | 'left' | 'right';

/** The box's name without the unit: "Dipstick before oil", "Dipstick left", "Dipstick right". */
export function dipstickName(engine: DipstickEngine): string {
  return engine === 'single' ? 'Dipstick before oil' : engine === 'left' ? 'Dipstick left' : 'Dipstick right';
}

/** The box's label: "Dipstick before oil (qt)", or with no unit set "Dipstick before oil". */
export function dipstickLabel(engine: DipstickEngine, unit: DipstickUnit | null): string {
  return unit ? `${dipstickName(engine)} (${unit})` : dipstickName(engine);
}

/** A reading as typed: blank is null (not read), anything else the number, or NaN when it is not one. */
export function dipstickFromText(text: string): number | null {
  const t = text.trim().replace(',', '.');
  if (t === '') return null;
  return /^\d+(\.\d+)?$|^\.\d+$/.test(t) ? Number(t) : Number.NaN;
}

/** The message for a reading that is not blank and not 0 to the unit's limit, else null. */
export function validateDipstick(
  value: number | null | undefined,
  unit: DipstickUnit | null,
  engine: DipstickEngine,
): string | null {
  if (value === null || value === undefined) return null;
  const max = unit ? DIPSTICK_MAX[unit] : DIPSTICK_MAX_NO_UNIT;
  if (Number.isFinite(value) && value >= 0 && value <= max) return null;
  return `${dipstickName(engine)} must be 0 to ${max}${unit ? ` ${unit}` : ''}, or blank.`;
}

/** The entry's dipstick problems, by field, for the boxes the aircraft shows (`entryShape`). */
export function dipstickIssues(
  entry: Pick<DailyEntry, 'entry_type' | 'oil_dipstick_l' | 'oil_dipstick_r'>,
  aircraft: Aircraft | null | undefined,
): { readonly field: 'oil_dipstick_l' | 'oil_dipstick_r'; readonly message: string }[] {
  const boxes = entryShape(entry.entry_type, aircraft ?? null).dipstickBoxes;
  const unit = dipstickUnit(aircraft);
  const out: { field: 'oil_dipstick_l' | 'oil_dipstick_r'; message: string }[] = [];
  const left = validateDipstick(entry.oil_dipstick_l, unit, boxes === 1 ? 'single' : 'left');
  if (left) out.push({ field: 'oil_dipstick_l', message: left });
  const right = validateDipstick(entry.oil_dipstick_r, unit, 'right');
  if (right) out.push({ field: 'oil_dipstick_r', message: right });
  return out;
}

/**
 * The line under the boxes when the aircraft has none set: "No dipstick unit set for VH-JOZ: a tenant admin or
 * manager sets it in the aircraft register." Null when a unit is set, or no box shows.
 */
export function dipstickNoUnitNote(aircraft: Aircraft | null | undefined): string | null {
  if (!aircraft || dipstickUnit(aircraft)) return null;
  if (entryShape('flight', aircraft).dipstickBoxes === 0) return null;
  return `No dipstick unit set for ${aircraft.registration}: a tenant admin or manager sets it in the aircraft register.`;
}

const trim = (n: number): string => String(Math.round(n * 100) / 100);

/** A reading in words: "6.5 qt", "6.5" with no unit, "–" when not read. */
export function dipstickWords(value: number | null | undefined, unit: DipstickUnit | null): string {
  if (value === null || value === undefined) return '–';
  return unit ? `${trim(value)} ${unit}` : trim(value);
}

/** One row of Fuel and maintenance's "Engine oil" table. */
export interface EngineOilRow {
  readonly entry_id: string;
  readonly date: string;
  readonly flt_num: number | null;
  readonly aircraft_id: string | null;
  readonly registration: string;
  /** Two engines: the readings are "left / right". */
  readonly twin: boolean;
  readonly unit: DipstickUnit | null;
  readonly dipstick_l: number | null;
  readonly dipstick_r: number | null;
  readonly oil_l: number;
  readonly oil_r: number;
  /** "6.5 qt", "6.5 qt / 7 qt" on a twin. */
  readonly dipstickText: string;
  /** "0.5 L", "0.5 L / 0 L" on a twin. */
  readonly oilText: string;
}

/**
 * Fuel and maintenance's "Engine oil": each flight entry with oil added or a dipstick reading, newest first (date,
 * then flight number), with the aircraft's dipstick unit. A twin is an aircraft whose entry shows two oil boxes, or an
 * entry holding a right-engine figure.
 */
export function engineOilRows(entries: readonly DailyEntry[], aircraft: readonly Aircraft[]): EngineOilRow[] {
  const byId = new Map(aircraft.map((a) => [a.id, a]));
  const rows: EngineOilRow[] = [];
  for (const e of entries) {
    if (e.deleted_at !== null || e.entry_type !== 'flight') continue;
    const dl = e.oil_dipstick_l ?? null;
    const dr = e.oil_dipstick_r ?? null;
    if (e.oil_l + e.oil_r <= 0 && dl === null && dr === null) continue;
    const a = e.aircraft_id ? (byId.get(e.aircraft_id) ?? null) : null;
    const unit = dipstickUnit(a);
    const twin = (a ? entryShape('flight', a).oilBoxes === 2 : false) || e.oil_r > 0 || dr !== null;
    const oil = (n: number): string => `${trim(n)} L`;
    rows.push({
      entry_id: e.id,
      date: e.date,
      flt_num: e.flt_num,
      aircraft_id: e.aircraft_id,
      registration: a?.registration ?? '',
      twin,
      unit,
      dipstick_l: dl,
      dipstick_r: dr,
      oil_l: e.oil_l,
      oil_r: e.oil_r,
      dipstickText: twin
        ? `${dipstickWords(dl, unit)} / ${dipstickWords(dr, unit)}`
        : dipstickWords(dl, unit),
      oilText: twin ? `${oil(e.oil_l)} / ${oil(e.oil_r)}` : oil(e.oil_l),
    });
  }
  return rows.sort(
    (x, y) =>
      y.date.localeCompare(x.date) ||
      (y.flt_num ?? 0) - (x.flt_num ?? 0) ||
      x.entry_id.localeCompare(y.entry_id),
  );
}
